/**
 * DropPageSurface — a Drop rendered in PAGE style.
 *
 * Extracted out of `DropPageMain` the same way `DropHubSurface` was: the PAGE
 * tree is the other half of the same dispatch, and leaving it inline is what
 * kept `DropPageMain` over its own 400-line ceiling after the hub branch was
 * already pulled out.
 *
 * Takes the same props DropPageMain receives from DropPage, minus `style` (the
 * caller already branched on it to get here) and `slug` (unused — every
 * reference below reads `drop.slug`, not the raw param).
 */

import React, {useState} from "react";
import {TrackedSection} from "@/pages/drop/sections/withSectionTracking";
import SEO from "@/components/SEO";
import DevourTopNav from "@/components/DevourTopNav";
import LoginModal from "@/components/modals/LoginModal";
import {ChallengeLoginModal} from "@/components/modals/ChallengeLoginModal";
import {InviteModal, InviteModalType} from "@/components/InviteModal";
import {useCreateDropOptIn} from "@/hooks/drop/useCreateDropOptIn";
import {DropTermsAcceptModal} from "@/pages/drop/DropTermsAcceptModal";
import {useGetDropMemberStatus} from "@/hooks/drop/useGetDropMemberStatus";

import {DropInSubRail} from "@/widgets/DropInSubRail";
import {Header} from "@/widgets/Header";
import {Hero} from "@/widgets/Hero";
import {Nav} from "@/widgets/Nav";
import {PulseBar} from "@/widgets/PulseBar";
import {StreamingSection} from "@/widgets/StreamingSection";
import {ContentBlockRenderer} from "@/widgets/ContentBlockRenderer";
import {placedBoardId} from "@/surfaces/placedBoard";
import {ThemeProvider, type TenantId} from "@/widgets/ThemeProvider";
import ChallengePromotionsSection from "@/pages/challenge/ChallengePromotionsSection";

import {SECTION_ICONS} from "./DropSectionRenderer";
import {dropLayout} from "./dropLayout";
import {OwnerSocialDiscoveryCTA} from "./OwnerSocialDiscoveryCTA";
import {DropPageHero} from "./DropPageHero";

import type {DropSection} from "@/hooks/drop/useGetDrop";
import type {StreamingSettings} from "@/hooks/hub/useGetHub";

export interface DropPageSurfaceProps {
    drop: any;
    tenantId?: TenantId;
    isPublicHub: boolean;
    isLoginOpen: boolean;
    dispatch: (action: any) => any;
    enabledSections: DropSection[];
    streamingSection?: DropSection;
    infoSection?: DropSection;
    activeSection: string | null;
    setActiveSection: (id: string | null) => void;
    currentSection?: DropSection | null;
    renderSectionContent: (section: DropSection) => React.ReactNode;
    hasLiveStreams: boolean;
    pulse: any;
    pulseLoading: boolean;
    discordOnlineCount?: number;
    user?: {
        id?: string;
        experiencePoints?: number;
        level?: number;
        nickname?: string;
        firstName?: string;
        lastName?: string;
        gravatar?: string;
    };
    handleLoginRequired: () => void;
    handleInviteClick: () => void;
    referralUserId?: string;
    resolvedHeroImageUrl?: string;
    pointDisplay: {
        pointsName: string;
        pointsIconUrl: string | undefined;
        currentPoints: number;
        currentLevel: number;
        nextLevelPoints: number;
    };
    activePageBlocks: any[];
    isMultiPage: boolean;
    pageLabels: string[];
    activePageIndex: number;
    setActivePageIndex: (i: number) => void;
    isInviteModalOpen: boolean;
    setIsInviteModalOpen: (open: boolean) => void;
    missionsChallenge?: any;
    toggleLoginOpen: (open: boolean) => any;
}

export const DropPageSurface: React.FC<DropPageSurfaceProps> = ({
    drop, tenantId, isPublicHub, isLoginOpen, dispatch,
    enabledSections, streamingSection, infoSection, activeSection, setActiveSection, currentSection, renderSectionContent,
    hasLiveStreams, pulse, pulseLoading, discordOnlineCount,
    user, handleLoginRequired, handleInviteClick, referralUserId, resolvedHeroImageUrl, pointDisplay,
    activePageBlocks, isMultiPage, pageLabels, activePageIndex, setActivePageIndex,
    isInviteModalOpen, setIsInviteModalOpen, missionsChallenge, toggleLoginOpen,
}) => {
    /* Same door wiring as the HUB branch — keep the two trees in step. */
    const dropOptIn = useCreateDropOptIn();
    const [termsDismissed, setTermsDismissed] = useState(false);
    const dropMemberStatus = useGetDropMemberStatus(drop?.slug, Boolean(drop?.onboardingExperienceId));
    /*
     * The POV sets the design. See `dropLayout` for why this is one call and
     * not three booleans read in three places.
     */
    const layout = dropLayout(drop);
    const isPublisher = layout === "PUBLISHER";

    return (
        <ThemeProvider themeColors={drop.themeColors} typography={drop.typography} themeConfig={drop.themeConfig} tenantId={tenantId}>
            <SEO
                title={drop.seo?.title || `${drop.name} | DevourPlay`}
                description={drop.seo?.description || drop.description || `${drop.name} gaming hub`}
                url={`https://devourplay.gg/drop-in/${drop.slug}`}
                imageUrl={drop.seo?.socialShareImageUrl || drop.heroSection?.heroImageUrl}
            />

            {/*
         * Global sidebar deliberately NOT rendered. The 5-tab verb-oriented
         * topbar (PLAY · DROP-IN · COMPETE · STASH · LOOT) now owns global
         * navigation. The DevourPlaySidebar is hidden via PageWrapper's
         * hideDevourPlaySidebar prop from DropPageRouter.
         */}
            {isPublicHub && <DevourTopNav text={drop.name} />}

            {/*
          * The Drop's own accent, published once for every widget below.
          *
          * Each publisher widget reads `var(--drop-accent, …)`. Nothing was
          * setting it, so all of them silently used their fallback — which
          * meant a page themed in admin still rendered in the default colour.
          * Set here rather than per-widget because it is one value the whole
          * page shares, and a per-widget prop would be a value to forget.
          */}
            <div
                className="drop-page-layout"
                style={drop.themeColors?.primary
                    ? {"--drop-accent": drop.themeColors.primary} as React.CSSProperties
                    : undefined}
            >
                <DropInSubRail activeHandle={drop.slug} />
                <div className={`drop-page ${isPublicHub ? "drop-page--public" : "drop-page--private"}`}>

                    {/*
                      * PRIVATE hubs mount their own login (no PageWrapper).
                      * A Drop carrying an onboarding experience gets the
                      * BRANDED sequence (code + welcome emails from its
                      * experience templates, sent via dropSlug); every other
                      * Drop keeps the standard platform modal.
                      */}
                    {!isPublicHub && (drop.onboardingExperienceId
                        ? <ChallengeLoginModal
                            isOpen={isLoginOpen}
                            onClose={() => dispatch(toggleLoginOpen(false))}
                            dropSlug={drop.slug}
                            /* Same opt-in-on-login as the HUB branch — keep in step. */
                            onLoginSuccess={data => {
                                dropOptIn.mutate({
                                    slug: drop.slug,
                                    declaredCountry: data?.declaredCountry,
                                    declaredState: data?.declaredState,
                                    termsVersion: dropMemberStatus.data?.requiredTermsVersion,
                                }, {
                                    /*
                                     * The email door writes no membership server-side, so a
                                     * silent failure here leaves a signed-in member earning
                                     * nothing and off every board. Re-read so the surface can
                                     * ask again.
                                     */
                                    onError: () => dropMemberStatus.refetch(),
                                });
                                dispatch(toggleLoginOpen(false));
                            }}
                            geoFirst
                            /*
                             * Everyone is eligible here. This surface resets weekly and nobody
                             * advances off it to a US Finals seat, so the Race's US-only prize
                             * framing would be false — declared rather than inherited, because
                             * geoFirst forces admitAllCountries true and that used to be read
                             * as a prize rule.
                             */
                            prizeEligibility="OPEN"
                            memberStatus={dropMemberStatus.data}
                            title={dropMemberStatus.data?.hasMembership
                                ? `Welcome back to ${drop.name}`
                                : `Welcome to ${drop.name}`}
                            description="Sign in to start earning points, join community activities and climb the board."
                            /*
                             * Same drop-supplied brand face as the HUB branch
                             * in DropPageMain — keep the two in step.
                             */
                            challenge={{
                                brandColor: drop.themeColors?.primary,
                                allowedSignupMethods: ["EMAIL"],
                            } as any}
                        />
                        : <LoginModal
                            isOpen={isLoginOpen}
                            onClose={() => dispatch(toggleLoginOpen(false))}
                        />)}

                    {/* Header — only for PRIVATE hubs (PUBLIC uses DevourTopNav). */}
                    {!isPublicHub &&
                    <Header
                        hubName={drop.name}
                        logoUrl={drop.creatorProfileImageUrl}
                        navbarConfig={drop.navbarConfig}
                        onLoginRequired={handleLoginRequired}
                        streamingSectionId={streamingSection?._id}
                        hasLiveStreams={hasLiveStreams}
                        infoSectionId={infoSection?._id}
                        activeSection={activeSection}
                        onSectionChange={setActiveSection}
                        onScrollToStreaming={() => {
                            /* If we're on a section page, go back to home first then scroll. */
                            if (activeSection) {
                                setActiveSection(null);
                                setTimeout(() => {
                                    const w = document.querySelector(".drop-page__streaming-wrapper");
                                    if (w) w.scrollIntoView({behavior: "smooth"});
                                }, 100);
                            } else {
                                const w = document.querySelector(".drop-page__streaming-wrapper");
                                if (w) w.scrollIntoView({behavior: "smooth"});
                            }
                        }}
                        pointsName={pointDisplay.pointsName}
                        pointsIconUrl={pointDisplay.pointsIconUrl}
                        currentPoints={pointDisplay.currentPoints}
                        currentLevel={pointDisplay.currentLevel}
                        nextLevelPoints={pointDisplay.nextLevelPoints}
                        themeColors={drop.themeColors}
                        onViewRewards={() => {
                            const r = document.querySelector(".rewards-section");
                            if (r) r.scrollIntoView({behavior: "smooth"});
                        }}
                        onInviteClick={handleInviteClick}
                    />
                    }

                    <DropPageHero
                        drop={drop}
                        layout={layout}
                        hasLiveStreams={hasLiveStreams}
                        pulse={pulse}
                        handleLoginRequired={handleLoginRequired}
                        handleInviteClick={handleInviteClick}
                        referralUserId={referralUserId}
                        discordOnlineCount={discordOnlineCount}
                    />

                    {/* Owner-only social discovery CTA — hard-gates on userId match. */}
                    {drop.id && drop.socials &&
                    <OwnerSocialDiscoveryCTA
                        dropId={drop.id}
                        dropSlug={drop.slug}
                        dropOwnerId={drop.ownerId}
                        currentUserId={user?.id}
                        seedPlatform={(drop.socials.find((s: any) => s.platform === "TWITCH" || s.platform === "KICK")?.platform as "TWITCH" | "KICK" | undefined) ?? "TWITCH"}
                        seedUsername={drop.socials.find((s: any) => s.platform === "TWITCH")?.username ?? drop.socials.find((s: any) => s.platform === "KICK")?.username ?? ""}
                        presentSocialPlatforms={new Set(drop.socials.map((s: any) => s.platform))}
                    />
                    }

                    {/* Hub furniture. A publisher page is a composed editorial
                    surface, not a hub dashboard — see the isPublisher note. */}
                    {!isPublisher && <PulseBar pulse={pulse} isLoading={pulseLoading} />}

                    {/*
                      * Page-level Nav skipped for CREATOR hubs (CreatorSection
                      * handles its own) and for PUBLISHER pages, which stack
                      * every beat in one scroll instead of hiding them behind
                      * tabs.
                      */}
                    {drop.dropType !== "CREATOR" && !isPublisher &&
                    <Nav
                        /*
                         * Nav typed against HubSection (Hub-tree generated
                         * type) but the structural shape matches DropSection
                         * for the fields Nav reads (_id, label, type, icon).
                         * Cast at the boundary — same pattern DropPage used
                         * before the extract.
                         */
                        sections={enabledSections as unknown as Parameters<typeof Nav>[0]["sections"]}
                        activeSection={activeSection}
                        onSectionChange={setActiveSection}
                        onHomeClick={() => setActiveSection(null)}
                        sectionIcons={SECTION_ICONS}
                        mobileNavStyle={drop.navbarConfig?.mobileNavStyle}
                    />
                    }

                    {/* Home-view streaming module — non-CREATOR hubs only. CREATOR
                  * hubs render their streaming through the section enum dispatch. */}
                    {!activeSection && drop.dropType !== "CREATOR" && !isPublisher &&
                    <div className="drop-page__streaming-wrapper">
                        <StreamingSection
                            streamingSettings={drop.streamingSettings as unknown as StreamingSettings}
                            hubName={drop.name}
                            hubType={drop.dropType}
                            surfaceId={drop.id}
                        />
                    </div>
                    }

                    {/*
                      * PUBLISHER: every beat, stacked, in configured order.
                      *
                      * The anchor is one continuous scroll — hero, the
                      * challenge, the creator team, news, other titles —
                      * because each beat feeds the next: the team drives the
                      * engagement, which promotes the game, which grows the
                      * team. Putting those behind tabs breaks the argument
                      * into five pages a visitor has to assemble themselves,
                      * and lands them on a HOME tab that shows none of it.
                      *
                      * Every other POV keeps the tabbed single-section
                      * behaviour.
                      */}
                    {isPublisher
                        ? <div className="drop-page__content drop-page__content--stacked">
                            {/*
                              * Sections that render nothing are skipped, not
                              * wrapped.
                              *
                              * Every enabled section used to get a `__beat`
                              * wrapper unconditionally. A section that returns
                              * null — an unassigned engagement, a news feed
                              * with no Steam id — still produced an empty flex
                              * child, and the column has a 44px gap, so two
                              * empty beats left ~88px of dead scroll exactly
                              * where the page's loudest content should be.
                              */}
                            {enabledSections.map(section => {
                                const content = renderSectionContent(section);
                                if (!content) return null;
                                return (
                                    /*
                                     * Keyed on `type`, not `_id`: the id is a Mongo
                                     * ObjectId, which is unique per Drop and useless to
                                     * group by. `type` is the section kind and matches
                                     * what the hub archetype reports.
                                     */
                                    <TrackedSection
                                        key={section._id ?? section.type}
                                        sectionId={section.type}
                                        className="drop-page__beat"
                                    >
                                        {content}
                                    </TrackedSection>
                                );
                            })}
                        </div>
                        : <div className="drop-page__content">
                            {currentSection &&
                                /*
                                 * The tabbed branch — every POV except PUBLISHER, and the
                                 * one nearly all traffic gets. Instrumenting only the
                                 * stacked branch above left it silent: 15 of 15
                                 * /drop-in/:slug arrivals after 1.2.24239 reported no
                                 * section at all.
                                 *
                                 * `key` is load-bearing. One element swaps its content as
                                 * tabs change, and the observer reports once per MOUNT, so
                                 * without a key that changes per section only the first
                                 * tab a visitor lands on would ever be recorded.
                                 *
                                 * Here "seen" means "selected", which is the stronger
                                 * signal anyway — a tab someone opened, not one that
                                 * happened to scroll past.
                                 */
                                <TrackedSection
                                    key={currentSection.type}
                                    sectionId={currentSection.type}
                                >
                                    {renderSectionContent(currentSection)}
                                </TrackedSection>
                            }
                        </div>
                    }

                    {/* Dynamic Content Blocks — only when no section is active. */}
                    {!activeSection && activePageBlocks.length > 0 &&
                    <div className="drop-page__content-blocks">
                        {isMultiPage &&
                            <div className="drop-page__page-tabs">
                                {pageLabels.map((label, i) =>
                                    <button
                                        key={i}
                                        type="button"
                                        className={`drop-page__page-tab ${activePageIndex === i ? "drop-page__page-tab--active" : ""}`}
                                        onClick={() => setActivePageIndex(i)}
                                    >
                                        {label}
                                    </button>)}
                            </div>
                        }
                        <ContentBlockRenderer
                            blocks={activePageBlocks}
                            themeColors={drop.themeColors}
                            brandName={drop.name}
                            surfaceSlug={drop.slug}
                            pointDisplay={pointDisplay}
                            hubPhase={drop.phase}
                            /* The board this Drop places — the earn section's cards come from its assigned objectives. */
                            ledgerBoardId={placedBoardId(drop.sections, activePageBlocks)}
                        />
                    </div>
                    }

                    <InviteModal
                        isVisible={isInviteModalOpen}
                        type={InviteModalType.HUB}
                        onClose={() => setIsInviteModalOpen(false)}
                        hubData={{
                            slug: drop.slug,
                            name: drop.name,
                            backgroundUrl: resolvedHeroImageUrl,
                        }}
                        alwaysUseDarkBackground={true}
                        brandColorOverride={drop.themeColors?.primary}
                    />

                    {!activeSection && missionsChallenge &&
                    <div className="drop-page__explore-wrapper">
                        <ChallengePromotionsSection challenge={missionsChallenge} />
                    </div>
                    }
                </div>
            </div>
            {/*
              * The PAGE branch mounts the same door as HUB and must ask for
              * terms the same way. It did not, so a returning Realm member —
              * signed in without ever being shown the rules — had no route to
              * attest, and `resolveOptInMemberIds` kept them off the board and
              * out of the payout with nothing on screen to explain it.
              */}
            <DropTermsAcceptModal
                isOpen={Boolean(dropMemberStatus.data?.hasMembership) &&
                    dropMemberStatus.data?.attestationOnFile === false &&
                    !termsDismissed}
                dropName={drop.name}
                officialRulesUrl={dropMemberStatus.data?.officialRulesUrl}
                isSubmitting={dropOptIn.isPending}
                onDismiss={() => setTermsDismissed(true)}
                onAccept={() => {
                    dropOptIn.mutate({
                        slug: drop.slug,
                        termsVersion: dropMemberStatus.data?.requiredTermsVersion,
                    }, {
                        /* Refetch rather than assume — the server decides whether it counted. */
                        onSuccess: () => dropMemberStatus.refetch(),
                    });
                }}
            />
        </ThemeProvider>
    );
};

export default DropPageSurface;
